import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalOffer as DiscountIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import discountService from '../services/discountService';
import { productService } from '../services/productService';
import LoadingSpinner from '../components/LoadingSpinner';

const Discounts = () => {
  const navigate = useNavigate();
  const [discounts, setDiscounts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [discountToDelete, setDiscountToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    promoCode: ''
  });

  useEffect(() => {
    fetchData();
    fetchProducts();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      setLoading(true);
      const [discountsResponse, statsResponse] = await Promise.all([
        discountService.getAllDiscounts(filters),
        discountService.getDiscountStats()
      ]);
      
      console.log('Discounts response:', discountsResponse);
      setDiscounts(Array.isArray(discountsResponse.data) ? discountsResponse.data : []);
      setStats(statsResponse.data || {});
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Error fetching discounts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      // Products are fetched but not stored in state as they're not used in this component
      await productService.getAllProducts();
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddDiscount = () => {
    navigate('/discounts/add');
  };

  const handleEditDiscount = (discount) => {
    navigate(`/discounts/edit/${discount._id}`);
  };

  const handleDelete = async () => {
    try {
      await discountService.deleteDiscount(discountToDelete._id);
      showSnackbar('Discount deleted successfully');
      setDeleteDialogOpen(false);
      setDiscountToDelete(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting discount:', error);
      showSnackbar(error.response?.data?.message || 'Error deleting discount', 'error');
    }
  };

  const getStatusChip = (discount) => {
    const now = new Date();
    const startDate = new Date(discount.startDate);
    const endDate = new Date(discount.endDate);

    if (!discount.isActive) {
      return <Chip label="Inactive" color="default" size="small" />;
    }

    if (endDate < now) {
      return <Chip label="Expired" color="error" size="small" />;
    }

    if (startDate > now) {
      return <Chip label="Upcoming" color="warning" size="small" />;
    }

    if (discount.usageLimit && discount.usedCount >= discount.usageLimit) {
      return <Chip label="Limit Reached" color="error" size="small" />;
    }

    return <Chip label="Active" color="success" size="small" />;
  };

  const formatDiscountValue = (discount) => {
    if (discount.discountMode === 'percentage') {
      return `${discount.value}%`;
    }
    return `₹${discount.value}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      timeZone: "Asia/Kolkata"
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Discount Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddDiscount}
        >
          Add Discount
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <DiscountIcon color="primary" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.totalDiscounts || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Discounts
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">{stats.activeDiscounts || 0}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Active
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <ScheduleIcon color="warning" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">{stats.upcomingDiscounts || 0}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Upcoming
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <TrendingUpIcon color="info" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">{stats.flatDiscounts || 0}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Flat Discounts
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <DiscountIcon color="secondary" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">{stats.couponDiscounts || 0}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Coupon Discounts
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.type}
                  onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                  label="Type"
                >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="flat">Flat</MenuItem>
                <MenuItem value="coupon">Coupon</MenuItem>
                <MenuItem value="buy_x_get_y">Buy X Get Y</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                  <MenuItem value="upcoming">Upcoming</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Search Promo Code"
                value={filters.promoCode}
                onChange={(e) => setFilters(prev => ({ ...prev, promoCode: e.target.value }))}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Discounts Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Promo Code</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Products</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(discounts || []).map((discount) => (
                <TableRow key={discount._id}>
                  <TableCell>
                  <Chip 
                    label={
                      discount.type === 'flat'
                        ? 'Flat'
                        : discount.type === 'coupon'
                          ? 'Coupon'
                          : 'Buy X Get Y'
                    }
                    color={
                      discount.type === 'flat'
                        ? 'primary'
                        : discount.type === 'coupon'
                          ? 'secondary'
                          : 'info'
                    }
                    size="small"
                  />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={discount.discountMode === 'percentage' ? 'Percentage' : 'Amount'} 
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDiscountValue(discount)}</TableCell>
                  <TableCell>{discount.promoCode || '-'}</TableCell>
                  <TableCell>{formatDate(discount.startDate)}</TableCell>
                  <TableCell>{formatDate(discount.endDate)}</TableCell>
                  <TableCell>
                    {discount.products?.length > 0 ? `${discount.products.length} products` : 'All products'}
                  </TableCell>
                  <TableCell>{getStatusChip(discount)}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton 
                        size="small" 
                        onClick={() => handleEditDiscount(discount)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => {
                          setDiscountToDelete(discount);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Discount</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this discount? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Discounts;
